import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============= FUNCIONES DE FORMATEO Y VALIDACIÓN =============

// Detectar si es RUT chileno o CUIT argentino basado en el patrón
const detectarTipo = (value) => {
  const clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  // CUIT argentino tiene 11 dígitos
  if (clean.length === 11 && /^\d{11}$/.test(clean)) {
    return 'CUIT';
  }
  // RUT chileno tiene 8-9 caracteres (puede terminar en K)
  if (clean.length >= 8 && clean.length <= 9) {
    return 'RUT';
  }
  // Por defecto, si tiene menos caracteres, intentar determinar por el contenido
  if (clean.length > 0) {
    // Si empieza con 20, 23, 24, 27, 30, 33, 34 probablemente es CUIT
    const prefix = clean.substring(0, 2);
    if (['20', '23', '24', '27', '30', '33', '34'].includes(prefix) && clean.length >= 10) {
      return 'CUIT';
    }
  }
  return 'RUT'; // Por defecto RUT
};

// Formatear RUT chileno (xx.xxx.xxx-x)
const formatRut = (value) => {
  let clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length === 0) return '';
  
  // Limitar a 9 caracteres
  clean = clean.slice(0, 9);
  
  let dv = clean.slice(-1);
  let body = clean.slice(0, -1);
  
  let formatted = '';
  let count = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    if (count > 0 && count % 3 === 0) {
      formatted = '.' + formatted;
    }
    formatted = body[i] + formatted;
    count++;
  }
  
  if (clean.length > 1) {
    return `${formatted}-${dv}`;
  }
  
  return formatted || dv;
};

// Formatear CUIT argentino (xx-xxxxxxxx-x)
const formatCuit = (value) => {
  let clean = value.replace(/[^0-9]/g, '');
  if (clean.length === 0) return '';
  
  // Limitar a 11 dígitos
  clean = clean.slice(0, 11);
  
  let formatted = '';
  
  if (clean.length >= 1) {
    formatted = clean.slice(0, 2);
  }
  if (clean.length > 2) {
    formatted += '-' + clean.slice(2, 10);
  }
  if (clean.length > 10) {
    formatted += '-' + clean.slice(10, 11);
  }
  
  return formatted;
};

// Validar RUT chileno
const validarRut = (rutCompleto) => {
  if (!rutCompleto || rutCompleto.length < 3) return false;
  
  const rutLimpio = rutCompleto.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 2) return false;
  
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1);
  
  if (!/^\d+$/.test(cuerpo)) return false;
  
  let suma = 0;
  let multiplicador = 2;
  
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const resto = suma % 11;
  const dvCalculado = 11 - resto;
  
  let dvEsperado;
  if (dvCalculado === 11) dvEsperado = '0';
  else if (dvCalculado === 10) dvEsperado = 'K';
  else dvEsperado = dvCalculado.toString();
  
  return dv === dvEsperado;
};

// Validar CUIT argentino
const validarCuit = (cuitCompleto) => {
  if (!cuitCompleto) return false;
  
  const cuitLimpio = cuitCompleto.replace(/[^0-9]/g, '');
  if (cuitLimpio.length !== 11) return false;
  
  // Verificar que empiece con un prefijo válido
  const prefijo = cuitLimpio.substring(0, 2);
  const prefijosValidos = ['20', '23', '24', '27', '30', '33', '34'];
  if (!prefijosValidos.includes(prefijo)) return false;
  
  // Algoritmo de validación CUIT argentino
  const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let suma = 0;
  
  for (let i = 0; i < 10; i++) {
    suma += parseInt(cuitLimpio[i]) * multiplicadores[i];
  }
  
  const resto = suma % 11;
  let dvCalculado = 11 - resto;
  
  // Ajustar según las reglas del CUIT
  if (dvCalculado === 11) dvCalculado = 0;
  if (dvCalculado === 10) dvCalculado = 9;
  
  const dvIngresado = parseInt(cuitLimpio[10]);
  
  return dvIngresado === dvCalculado;
};

// Formatear automáticamente según el tipo detectado
const formatRutCuit = (value) => {
  const clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  
  // Si parece ser CUIT (11 dígitos numéricos o empieza con prefijo CUIT)
  const prefix = clean.substring(0, 2);
  const esCuit = ['20', '23', '24', '27', '30', '33', '34'].includes(prefix) && 
                 clean.length >= 3 && 
                 /^\d+$/.test(clean);
  
  if (esCuit || clean.length === 11) {
    return formatCuit(value);
  }
  
  return formatRut(value);
};

// Validar según el formato detectado
const validarRutCuit = (value) => {
  if (!value || value.length < 3) return { valid: false, tipo: null, mensaje: '' };
  
  const clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  
  // Detectar si es CUIT
  const prefix = clean.substring(0, 2);
  const esCuit = ['20', '23', '24', '27', '30', '33', '34'].includes(prefix) && /^\d+$/.test(clean);
  
  if (esCuit || clean.length === 11) {
    const valid = validarCuit(value);
    return { 
      valid, 
      tipo: 'CUIT', 
      mensaje: valid ? '' : 'CUIT inválido' 
    };
  }
  
  // Es RUT
  const valid = validarRut(value);
  return { 
    valid, 
    tipo: 'RUT', 
    mensaje: valid ? '' : 'RUT inválido' 
  };
};

// ============= COMPONENTE PRINCIPAL =============

const Proveedores = () => {
  const { user: currentUser } = useAuth();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [formData, setFormData] = useState({
    razon_social: '', rut: '', alias: '', giro: '', direccion: '', comuna: '',
    contacto_nombre: '', contacto_telefono: '', email: ''
  });
  const [justification, setJustification] = useState('');
  
  // Estado para el RUT/CUIT formateado
  const [rutCuitDisplay, setRutCuitDisplay] = useState('');
  const [rutCuitError, setRutCuitError] = useState('');
  const [rutCuitTipo, setRutCuitTipo] = useState('');

  useEffect(() => { fetchProveedores(); }, []);

  const fetchProveedores = async () => {
    try {
      const res = await axios.get(`${API}/proveedores`);
      setProveedores(res.data);
    } catch { toast.error('Error al cargar proveedores'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ razon_social: '', rut: '', alias: '', giro: '', direccion: '', comuna: '', contacto_nombre: '', contacto_telefono: '', email: '' });
    setJustification('');
    setSelectedProveedor(null);
    setRutCuitDisplay('');
    setRutCuitError('');
    setRutCuitTipo('');
  };

  const handleRutCuitChange = (e) => {
    const value = e.target.value;
    const formatted = formatRutCuit(value);
    setRutCuitDisplay(formatted);
    setFormData(prev => ({ ...prev, rut: formatted }));
    
    // Validar
    if (formatted.length >= 3) {
      const { valid, tipo, mensaje } = validarRutCuit(formatted);
      setRutCuitError(mensaje);
      setRutCuitTipo(tipo);
    } else {
      setRutCuitError('');
      setRutCuitTipo('');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validar RUT/CUIT antes de enviar
    const { valid, mensaje } = validarRutCuit(formData.rut);
    if (!valid) {
      toast.error(mensaje || 'RUT/CUIT inválido');
      return;
    }
    
    try {
      await axios.post(`${API}/proveedores`, formData);
      toast.success('Proveedor creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      fetchProveedores();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear proveedor'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) { toast.error('La justificación es obligatoria'); return; }
    
    // Validar RUT/CUIT antes de enviar
    const { valid, mensaje } = validarRutCuit(formData.rut);
    if (!valid) {
      toast.error(mensaje || 'RUT/CUIT inválido');
      return;
    }
    
    try {
      await axios.put(`${API}/proveedores/${selectedProveedor.id}`, { justification, data: formData });
      toast.success('Proveedor actualizado');
      setIsEditModalOpen(false);
      resetForm();
      fetchProveedores();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este proveedor?')) return;
    try {
      await axios.delete(`${API}/proveedores/${id}`);
      toast.success('Proveedor eliminado');
      fetchProveedores();
    } catch { toast.error('Error al eliminar'); }
  };

  const openEditModal = (p) => {
    setSelectedProveedor(p);
    setFormData({
      razon_social: p.razon_social, rut: p.rut, alias: p.alias || '', giro: p.giro,
      direccion: p.direccion, comuna: p.comuna || '', contacto_nombre: p.contacto_nombre || '',
      contacto_telefono: p.contacto_telefono || '', email: p.email || ''
    });
    setRutCuitDisplay(p.rut || '');
    // Detectar tipo
    const { tipo } = validarRutCuit(p.rut);
    setRutCuitTipo(tipo);
    setIsEditModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleFieldChange = (field, value) => {
    const upper = ['razon_social', 'alias', 'giro', 'direccion', 'comuna', 'contacto_nombre'];
    setFormData(prev => ({ ...prev, [field]: upper.includes(field) ? value.toUpperCase() : value }));
  };

  const formFields = (prefix = '') => (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Razón Social *</Label>
        <Input value={formData.razon_social} onChange={e => handleFieldChange('razon_social', e.target.value)} required className="uppercase" data-testid={`${prefix}proveedor-razon-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">RUT / CUIT *</Label>
        <Input 
          value={rutCuitDisplay} 
          onChange={handleRutCuitChange} 
          required 
          placeholder="12.345.678-9 o 20-12345678-9" 
          className={rutCuitError ? 'border-red-500' : ''}
          data-testid={`${prefix}proveedor-rut-input`} 
        />
        <div className="flex items-center justify-between">
          {rutCuitError && <p className="text-[10px] text-red-500">{rutCuitError}</p>}
          {rutCuitTipo && !rutCuitError && (
            <p className="text-[10px] text-emerald-600">✓ {rutCuitTipo} válido</p>
          )}
          <p className="text-[10px] text-slate-400 ml-auto">
            RUT: XX.XXX.XXX-X | CUIT: XX-XXXXXXXX-X
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Alias</Label>
        <Input value={formData.alias} onChange={e => handleFieldChange('alias', e.target.value)} className="uppercase" data-testid={`${prefix}proveedor-alias-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Giro *</Label>
        <Input value={formData.giro} onChange={e => handleFieldChange('giro', e.target.value)} required className="uppercase" data-testid={`${prefix}proveedor-giro-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Dirección *</Label>
        <Input value={formData.direccion} onChange={e => handleFieldChange('direccion', e.target.value)} required className="uppercase" data-testid={`${prefix}proveedor-direccion-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Comuna</Label>
        <Input value={formData.comuna} onChange={e => handleFieldChange('comuna', e.target.value)} className="uppercase" data-testid={`${prefix}proveedor-comuna-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Contacto</Label>
        <Input value={formData.contacto_nombre} onChange={e => handleFieldChange('contacto_nombre', e.target.value)} className="uppercase" data-testid={`${prefix}proveedor-contacto-input`} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Teléfono</Label>
        <Input value={formData.contacto_telefono} onChange={e => handleFieldChange('contacto_telefono', e.target.value)} data-testid={`${prefix}proveedor-telefono-input`} />
      </div>
      <div className="space-y-1.5 col-span-2">
        <Label className="text-xs">Email</Label>
        <Input type="email" value={formData.email} onChange={e => handleFieldChange('email', e.target.value)} data-testid={`${prefix}proveedor-email-input`} />
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="proveedores-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Proveedores</h1>
            <p className="text-slate-600 mt-1">Gestión de proveedores de grano</p>
          </div>
          {currentUser.role === 'MASTER' && (
            <Button onClick={openCreateModal} data-testid="create-proveedor-btn" className="bg-primary hover:bg-primary-hover text-white rounded-sm">
              <Plus className="mr-2" size={18} /> Nuevo Proveedor
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : proveedores.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
            <Store size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No hay proveedores registrados</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
            <table className="w-full" data-testid="proveedores-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Razón Social</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">RUT / CUIT</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Alias</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Giro</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {proveedores.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`proveedor-row-${p.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{p.razon_social}</td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{p.rut}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.alias || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.giro}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.contacto_nombre || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button onClick={() => openEditModal(p)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50" data-testid={`edit-proveedor-${p.id}`}>
                          <Pencil size={15} />
                        </Button>
                        {currentUser.role === 'MASTER' && (
                          <Button onClick={() => handleDelete(p.id)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-proveedor-${p.id}`}>
                            <Trash2 size={15} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-proveedor-modal">
            <DialogHeader><DialogTitle>Nuevo Proveedor</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {formFields()}
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-create-proveedor">
                Crear Proveedor
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-proveedor-modal">
            <DialogHeader><DialogTitle>Editar Proveedor</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              {formFields('edit-')}
              <div className="space-y-1.5">
                <Label className="text-xs text-red-600">Justificación (obligatoria) *</Label>
                <Input value={justification} onChange={e => setJustification(e.target.value)} required placeholder="Motivo de la edición" data-testid="edit-proveedor-justification" />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-edit-proveedor">
                Actualizar Proveedor
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Proveedores;
